import { get, put } from '@rover/react-lib/src/apis/favoriteSitters.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import type {
  FavoriteSitterRelationship,
  FavoriteSitterWithDistance,
  FetchFavoriteSittersResponse,
} from '@rover/types';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

type State = {
  errorMessage: string;
  loadingInitial: boolean;
  loadingNext: boolean;
  nextUrl: string | null | undefined;
  relationships: FavoriteSitterRelationship[];
};

type Actions = {
  fetchFavoriteSittersPending: ActionCreator<void>;
  fetchFavoriteSittersSuccess: ActionCreator<FetchFavoriteSittersResponse>;
  fetchFavoriteSittersFailure: ActionCreator<string>;
  fetchFavoriteSittersNextPending: ActionCreator<void>;
  fetchFavoriteSittersNextSuccess: ActionCreator<FetchFavoriteSittersResponse>;
  fetchFavoriteSittersNextFailure: ActionCreator<string>;
  removeFavoriteSitter: ActionCreator<string>;
  toggleFavoriteFailure: ActionCreator<string>;
};

type Effects = {
  fetchFavoriteSitters: EffectCreator<void>;
  fetchNextFavoriteSitters: EffectCreator<void>;
  toggleFavorite: EffectCreator<{
    asRequesterRelationshipUrl: string;
    isFavorite: boolean;
  }>;
};

type Selectors = {
  getFavoriteSittersRelationships: SelectorCreator<FavoriteSitterRelationship[]>;
  getFavoriteSittersWithDistance: SelectorCreator<FavoriteSitterWithDistance[]>;
  getLoadingInitial: SelectorCreator<boolean>;
  getLoadingNext: SelectorCreator<boolean>;
  getNextUrl: SelectorCreator<string | null | undefined>;
  getErrorMessage: SelectorCreator<string>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const combineResults = (
  currentResults: FavoriteSitterRelationship[],
  newResults: FavoriteSitterRelationship[]
): FavoriteSitterRelationship[] => {
  const combinedResults = currentResults
    .filter((r) => !newResults.find((n) => n.provider.url === r.provider.url))
    .concat(newResults);
  return combinedResults;
};

export const options: Options = {
  name: 'favoriteSitters',
  initialState: {
    errorMessage: '',
    loadingInitial: false,
    loadingNext: false,
    nextUrl: '',
    relationships: [],
  },
  actions: {
    fetchFavoriteSittersPending: () => (state) => ({
      ...state,
      loadingInitial: true,
      errorMessage: '',
    }),
    fetchFavoriteSittersSuccess: (response) => (state) => ({
      ...state,
      loadingInitial: false,
      relationships: response.results,
      nextUrl: response.next,
    }),
    fetchFavoriteSittersFailure: (errorMessage) => (state) => ({
      ...state,
      loadingInitial: false,
      errorMessage,
    }),
    fetchFavoriteSittersNextPending: () => (state) => ({
      ...state,
      loadingNext: true,
      errorMessage: '',
    }),
    fetchFavoriteSittersNextSuccess: (response) => (state) => ({
      ...state,
      loadingNext: false,
      relationships: combineResults(state.relationships, response.results),
      nextUrl: response.next,
    }),
    fetchFavoriteSittersNextFailure: (errorMessage) => (state) => ({
      ...state,
      loadingNext: false,
      errorMessage,
    }),
    removeFavoriteSitter: (asRequesterRelationshipUrl) => (state) => ({
      ...state,
      relationships: state.relationships.filter(
        (relationship) =>
          relationship.provider.urls.asRequesterRelationship !== asRequesterRelationshipUrl
      ),
    }),
    toggleFavoriteFailure: (errorMessage) => (state) => ({ ...state, errorMessage }),
  },
  effects: {
    fetchFavoriteSitters:
      () =>
      async (
        dispatch,
        getState,
        { fetchFavoriteSittersPending, fetchFavoriteSittersSuccess, fetchFavoriteSittersFailure }
      ) => {
        dispatch(fetchFavoriteSittersPending());

        try {
          const response: FetchFavoriteSittersResponse = await get();
          dispatch(fetchFavoriteSittersSuccess(response));
        } catch (e) {
          dispatch(fetchFavoriteSittersFailure((e as Error).message));
          captureError(e as Error);
        }
      },
    fetchNextFavoriteSitters:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchFavoriteSittersNextPending,
          fetchFavoriteSittersNextSuccess,
          fetchFavoriteSittersNextFailure,
        },
        duckEffects,
        { getNextUrl, getLoadingNext }
      ) => {
        const url = getNextUrl(getState());
        const isLoading = getLoadingNext(getState());

        if (url && url !== '' && !isLoading) {
          dispatch(fetchFavoriteSittersNextPending());

          try {
            const response: FetchFavoriteSittersResponse = await get(url);
            dispatch(fetchFavoriteSittersNextSuccess(response));
          } catch (e) {
            dispatch(fetchFavoriteSittersNextFailure((e as Error).message));
            captureError(e as Error);
          }
        }
      },
    toggleFavorite:
      ({
        asRequesterRelationshipUrl,
        isFavorite,
      }: {
        asRequesterRelationshipUrl: string;
        isFavorite: boolean;
      }) =>
      async (dispatch, getState, { removeFavoriteSitter, toggleFavoriteFailure }) => {
        if (isFavorite === false) {
          dispatch(removeFavoriteSitter(asRequesterRelationshipUrl));
        }

        try {
          await put(asRequesterRelationshipUrl, {
            isFavorite,
          });
        } catch (e) {
          dispatch(toggleFavoriteFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getFavoriteSittersRelationships: (getState, createSelector) =>
      createSelector([getState], (state) => state.relationships),
    getFavoriteSittersWithDistance: (
      getState,
      createSelector,
      { getFavoriteSittersRelationships }
    ) =>
      createSelector([getFavoriteSittersRelationships], (favoriteSittersRelationships) =>
        favoriteSittersRelationships.map((relationship) => ({
          ...relationship.provider,
          distanceDisplay: relationship.distanceDisplay,
        }))
      ),
    getLoadingInitial: (getState, createSelector) =>
      createSelector([getState], (state) => state.loadingInitial),
    getLoadingNext: (getState, createSelector) =>
      createSelector([getState], (state) => state.loadingNext),
    getNextUrl: (getState, createSelector) => createSelector([getState], (state) => state.nextUrl),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (state) => state.errorMessage),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
